import z from 'zod';
import { VIDEO_REPORT_MAX_DETAILS_LENGTH } from './constants';

// Video Report

export const VIDEO_REPORT_REASONS = ['offensive_language', 'nsfw_content', 'harassment_hate_speech', 'other'] as const;

export const videoReportReasonSchema = z.enum(VIDEO_REPORT_REASONS);
export type VideoReportReason = z.infer<typeof videoReportReasonSchema>;

export const videoReportRequestSchema = z
    .object({
        reason: videoReportReasonSchema,
        /** Required when `reason` is `other`. */
        details: z.string().max(VIDEO_REPORT_MAX_DETAILS_LENGTH).optional(),
        /** CDN file UUID derived from the playback URL, when available. */
        file_uuid: z.string().optional(),
    })
    .describe('VideoReportRequest');
export type VideoReportRequest = z.infer<typeof videoReportRequestSchema>;

export const videoReportResponseSchema = z
    .object({
        already_reported: z.boolean(),
        status: z.literal('recorded'),
    })
    .describe('VideoReportResponse');
export type VideoReportResponse = z.infer<typeof videoReportResponseSchema>;

// Video Feed

export const VIDEO_FEED_SOURCES = ['pool', 'curated_fallback'] as const;

export const videoFeedSourceSchema = z.enum(VIDEO_FEED_SOURCES);
export type VideoFeedSource = z.infer<typeof videoFeedSourceSchema>;

/**
 * Homepage popular-clips feed item. Every field except `externalId` may be omitted
 * (pool rows with missing columns, or `curated_fallback` items that are id-only).
 * Ranking bucket and score are never exposed.
 */
export const videoFeedItemSchema = z
    .object({
        externalId: z.string(),
        title: z.string().nullish(),
        playbackUrl: z.string().nullish(),
        thumbnailUrl: z.string().nullish(),
        recordedAt: z.string().nullish(),
        /** Alias used when `recordedAt` is omitted. */
        createdAt: z.string().nullish(),
        gameId: z.number().nullish(),
        titleId: z.number().nullish(),
        titleName: z.string().nullish(),
        clipTrigger: z.string().nullish(),
        clipEventTypes: z.string().nullish(),
    })
    .describe('VideoFeedItem');
export type VideoFeedItem = z.infer<typeof videoFeedItemSchema>;

export const videoFeedResponseSchema = z
    .object({
        servedDate: z.string(),
        /** Present when the worker tags pool vs curated fallback; omitted on 503-style clients. */
        source: videoFeedSourceSchema.optional(),
        items: z.array(videoFeedItemSchema),
    })
    .describe('VideoFeedResponse');
export type VideoFeedResponse = z.infer<typeof videoFeedResponseSchema>;
